import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DtcTextarea from "@/components/DtcTextarea";
import AuthImage from "@/components/AuthImage";
import RecallsPanel from "@/components/RecallsPanel";
import { toast } from "sonner";
import { CarProfile, MagnifyingGlass, ChatTeardropText, WarningOctagon, ArrowRight, ArrowLeft, Camera, X, Gauge, Toolbox } from "@phosphor-icons/react";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 1980 + 2 }, (_, i) => String(CURRENT_YEAR + 1 - i));

const COMMON_TOOLS = [
  "OBD-II Scan Tool (basic)",
  "Bidirectional Scan Tool",
  "Digital Multimeter (DMM)",
  "Oscilloscope",
  "Fuel Pressure Gauge",
  "Compression Tester",
  "Cylinder Leak-down Tester",
  "Vacuum Gauge",
  "EVAP Smoke Machine",
  "Infrared Thermometer",
  "Timing Light",
  "Test Light / Circuit Tester",
  "Battery / Charging Tester",
  "Borescope / Inspection Camera",
];

export default function NewDiagnostic() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [engine, setEngine] = useState("");
  const [engineOptions, setEngineOptions] = useState([]);
  const [engineLoading, setEngineLoading] = useState(false);
  const [engineCustom, setEngineCustom] = useState(false);
  const [vin, setVin] = useState("");
  const [decoding, setDecoding] = useState(false);
  const engineFromVin = useRef(false);
  const [vinBadge, setVinBadge] = useState(false); // mirrors engineFromVin for render
  const [complaint, setComplaint] = useState("");
  const [observations, setObservations] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [availableTools, setAvailableTools] = useState([]);

  const toggleTool = (tool) => {
    setAvailableTools((prev) =>
      prev.includes(tool) ? prev.filter((t) => t !== tool) : [...prev, tool]
    );
  };

  const decodeVin = async () => {
    if (!vin || vin.length !== 17) {
      toast.error("VIN must be 17 characters");
      return;
    }
    setDecoding(true);
    try {
      const { data } = await api.get(`/vin/${vin.toUpperCase()}`);
      setYear(data.year || year);
      setMake(data.make || make);
      setModel(data.model || model);
      if (data.engine) {
        engineFromVin.current = true;
        setVinBadge(true);
        setEngine(data.engine);
      }
      const engineSuffix = data.engine ? ` · ${data.engine}` : "";
      toast.success(`Decoded: ${data.year} ${data.make} ${data.model}${engineSuffix}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "VIN decode failed");
    } finally {
      setDecoding(false);
    }
  };

  const canNext1 = year && make && model;
  const canNext2 = complaint.trim().length > 5;

  // Fetch factory engine options for the entered Y/M/M via backend (AI + cache)
  useEffect(() => {
    if (!(year && make && model)) { setEngineOptions([]); return; }
    let cancelled = false;
    setEngineLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/vehicles/engines", { params: { year, make, model } });
        if (cancelled) return;
        const list = data.engines || [];
        setEngineOptions(list);
        // If engine came from VIN, keep it and switch to custom mode when it doesn't
        // exactly match a factory option (VIN strings like "5.0L V8" won't always match).
        if (engine && engineFromVin.current) {
          if (list.length && list.includes(engine)) {
            setEngineCustom(false);
          } else {
            setEngineCustom(true);
          }
        } else {
          setEngineCustom((prev) => (prev && !list.length) ? true : (list.length ? false : true));
          // Drop selection if it no longer matches (only when engine wasn't from VIN)
          if (engine && list.length && !list.includes(engine)) setEngine("");
        }
      } catch {
        if (!cancelled) { setEngineOptions([]); setEngineCustom(true); }
      } finally {
        if (!cancelled) setEngineLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, make, model]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/diagnostic/analyze", {
        year, make, model, engine, vin: vin || null, complaint, observations,
        photo_paths: photos.map((p) => p.path),
        available_tools: availableTools,
      });
      toast.success("Analysis ready");
      navigate(`/diagnostic/${data.diag_id}`);
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("Out of credits — top up in Billing to continue.");
        navigate("/billing");
      } else {
        toast.error(e?.response?.data?.detail || "Analysis failed. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (photos.length >= 4) { toast.error("Max 4 photos per case"); return; }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setPhotos((prev) => [...prev, { path: data.path, content_type: data.content_type }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingPhoto(false);
    }
  };
  const removePhoto = (path) => setPhotos((prev) => prev.filter((p) => p.path !== path));

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="text-xs font-mono-tech text-muted-foreground uppercase tracking-widest">// new case</div>
        <h1 className="font-display font-bold text-5xl tracking-tight">BUILD THE CASE</h1>
        <p className="text-muted-foreground mt-2">Three quick steps. The AI does the reasoning; you do the wrench work.</p>

        <Stepper step={step} />

        {step === 1 && (
          <section className="mt-8 border border-border rounded-md p-6 bg-card fade-up">
            <div className="flex items-center gap-2 mb-4">
              <CarProfile weight="bold" className="w-5 h-5 text-primary" />
              <div className="font-display text-2xl">Vehicle</div>
            </div>

            <div className="grid md:grid-cols-[1fr,auto] gap-3 items-end mb-6">
              <div>
                <Label htmlFor="vin" className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">VIN (optional)</Label>
                <Input
                  id="vin"
                  data-testid="vin-input"
                  value={vin}
                  onChange={(e) => setVin(e.target.value.toUpperCase().slice(0, 17))}
                  placeholder="1FTFW1E5XJKD12345"
                  className="mt-1 min-h-12 font-mono-tech bg-input"
                />
              </div>
              <Button type="button" onClick={decodeVin} disabled={decoding || vin.length !== 17} variant="outline" className="min-h-12" data-testid="decode-vin-btn">
                <MagnifyingGlass weight="bold" className="w-4 h-4 mr-2" />
                {decoding ? "Decoding..." : "Decode"}
              </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="year" className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">Year</Label>
                <select
                  id="year"
                  data-testid="year-select"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="mt-1 w-full min-h-12 px-3 rounded-md bg-input border border-border text-white"
                >
                  <option value="">Select year</option>
                  {YEARS.map((y) => (<option key={y} value={y}>{y}</option>))}
                </select>
              </div>
              <div>
                <Label htmlFor="make" className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">Make</Label>
                <Input id="make" data-testid="make-input" value={make} onChange={(e) => setMake(e.target.value)} placeholder="Ford" className="mt-1 min-h-12 bg-input" />
              </div>
              <div>
                <Label htmlFor="model" className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">Model</Label>
                <Input id="model" data-testid="model-input" value={model} onChange={(e) => setModel(e.target.value)} placeholder="F-150" className="mt-1 min-h-12 bg-input" />
              </div>
            </div>

            {/* Engine dropdown appears once Y/M/M are filled */}
            {canNext1 && (
              <div className="mt-4 fade-up">
                <Label htmlFor="engine" className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider flex items-center gap-2 flex-wrap">
                  <Gauge weight="bold" className="w-4 h-4 text-primary" /> Engine
                  {vinBadge && engine ? (
                    <span
                      data-testid="engine-vin-badge"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/15 border border-primary/40 text-primary text-[10px] tracking-widest uppercase"
                    >
                      <MagnifyingGlass weight="bold" className="w-3 h-3" /> VIN-decoded
                    </span>
                  ) : (
                    <span className="text-primary/80 normal-case tracking-normal">(recommended for accurate part suggestions)</span>
                  )}
                </Label>

                {engineLoading ? (
                  <div className="mt-1 border border-border rounded-md p-3 bg-input scan-line" data-testid="engine-loading">
                    <div className="font-mono-tech text-xs text-muted-foreground">// looking up factory engine options for {year} {make} {model}…</div>
                  </div>
                ) : engineCustom || engineOptions.length === 0 ? (
                  <div className="mt-1">
                    <Input
                      id="engine"
                      data-testid="engine-custom-input"
                      value={engine}
                      onChange={(e) => { engineFromVin.current = false; setVinBadge(false); setEngine(e.target.value); }}
                      placeholder='e.g. "3.5L V6 Turbo (EcoBoost)"'
                      className="min-h-12 bg-input font-mono-tech"
                    />
                    {engineOptions.length > 0 && (
                      <button
                        type="button"
                        onClick={() => { setEngineCustom(false); setEngine(""); }}
                        className="mt-1 text-xs text-primary hover:underline font-mono-tech"
                        data-testid="engine-use-list-btn"
                      >
                        ← pick from factory options
                      </button>
                    )}
                    {engineOptions.length === 0 && !engineLoading && (
                      <div className="text-[11px] text-muted-foreground mt-1 font-mono-tech">
                        No factory options found — enter the engine manually.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-1">
                    <select
                      id="engine"
                      data-testid="engine-select"
                      value={engine}
                      onChange={(e) => {
                        engineFromVin.current = false;
                        setVinBadge(false);
                        if (e.target.value === "__custom__") {
                          setEngineCustom(true);
                          setEngine("");
                        } else {
                          setEngine(e.target.value);
                        }
                      }}
                      className="w-full min-h-12 px-3 rounded-md bg-input border border-border text-white"
                    >
                      <option value="">Select engine option…</option>
                      {engineOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                      <option value="__custom__">— Other / custom —</option>
                    </select>
                    <div className="text-[11px] text-muted-foreground mt-1 font-mono-tech">
                      {engineOptions.length} factory option{engineOptions.length === 1 ? "" : "s"} for the {year} {make} {model}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recalls heads-up */}
            {canNext1 && <RecallsPanel year={year} make={make} model={model} />}

            <div className="flex justify-end mt-6">
              <Button onClick={() => setStep(2)} disabled={!canNext1} className="min-h-12 bg-primary hover:bg-primary/90" data-testid="wizard-next-1">
                Next <ArrowRight weight="bold" className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </section>
        )}

        {step === 2 && (
          <section className="mt-8 border border-border rounded-md p-6 bg-card fade-up">
            <div className="flex items-center gap-2 mb-4">
              <ChatTeardropText weight="bold" className="w-5 h-5 text-primary" />
              <div className="font-display text-2xl">Customer complaint</div>
            </div>
            <Label htmlFor="complaint" className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">What did the customer say?</Label>
            <Textarea
              id="complaint"
              data-testid="complaint-input"
              value={complaint}
              onChange={(e) => setComplaint(e.target.value)}
              placeholder="Engine misfires when warm, rough at idle, check engine light on..."
              className="mt-2 min-h-32 bg-input"
            />
            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setStep(1)} className="min-h-12" data-testid="wizard-back-2">
                <ArrowLeft weight="bold" className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button onClick={() => setStep(3)} disabled={!canNext2} className="min-h-12 bg-primary hover:bg-primary/90" data-testid="wizard-next-2">
                Next <ArrowRight weight="bold" className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="mt-8 border border-border rounded-md p-6 bg-card fade-up">
            <div className="flex items-center gap-2 mb-4">
              <WarningOctagon weight="bold" className="w-5 h-5 text-primary" />
              <div className="font-display text-2xl">Observations &amp; DTCs</div>
            </div>
            <Label htmlFor="obs" className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">Anything you've noticed, scanned, or already checked (optional) &mdash; type P/B/C/U codes for autocomplete</Label>
            <div className="mt-2">
              <DtcTextarea
                value={observations}
                onChange={setObservations}
                placeholder="DTC: P0302, P0316. Freeze frame shows 145°F ECT. Fuel trim +18% at idle. Plugs look fine..."
                testId="observations-input"
              />
            </div>

            {/* Photos */}
            <div className="mt-6">
              <Label className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Toolbox weight="bold" className="w-4 h-4" /> Tools on hand (optional)
              </Label>
              <div className="text-xs text-muted-foreground mt-1">
                Check what you have. The AI will design test procedures around it — with workarounds when the ideal tool isn't available.
                <span className="text-primary/80"> For best results, use the suggested tools noted in each step.</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="tool-picker">
                {COMMON_TOOLS.map((t) => {
                  const active = availableTools.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleTool(t)}
                      data-testid={`tool-chip-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      className={`text-xs font-mono-tech px-2.5 py-1.5 rounded-full border transition-colors ${
                        active
                          ? "bg-primary/15 border-primary/50 text-primary"
                          : "bg-transparent border-border text-muted-foreground hover:border-primary/40 hover:text-white"
                      }`}
                    >
                      {active ? "✓ " : "+ "}{t}
                    </button>
                  );
                })}
              </div>
              {availableTools.length > 0 && (
                <div className="text-[11px] text-muted-foreground mt-2 font-mono-tech">
                  {availableTools.length} tool{availableTools.length === 1 ? "" : "s"} selected · basic hand tools always assumed available
                </div>
              )}
            </div>

            <div className="mt-6">
              <Label className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Camera weight="bold" className="w-4 h-4" /> Attach photos (optional · max 4)
              </Label>
              <div className="mt-2 flex flex-wrap gap-3 items-start">
                {photos.map((p) => (
                  <div key={p.path} className="relative w-24 h-24 border border-border rounded-md overflow-hidden bg-background group" data-testid={`photo-thumb-${p.path}`}>
                    <AuthImage path={p.path} className="w-full h-full object-cover" alt="attachment" />
                    <button
                      type="button"
                      onClick={() => removePhoto(p.path)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-background/80 border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-photo-${p.path}`}
                    >
                      <X weight="bold" className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {photos.length < 4 && (
                  <label className="w-24 h-24 border border-dashed border-border rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors" data-testid="photo-upload-label">
                    <Camera weight="bold" className="w-5 h-5 text-muted-foreground" />
                    <span className="text-[10px] font-mono-tech text-muted-foreground mt-1">{uploadingPhoto ? "..." : "ADD"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
                      className="hidden"
                      data-testid="photo-upload-input"
                    />
                  </label>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-2">Photos of the scan tool, engine bay, or damaged parts help the AI reason more accurately.</div>
            </div>
            <div className="mt-6 border border-primary/30 bg-primary/5 rounded-md p-4 text-sm">
              <div className="font-mono-tech text-primary text-xs mb-1">READY TO ANALYZE</div>
              <div className="text-white/90">{year} {make} {model}{engine ? ` · ${engine}` : ""}{vin ? ` · VIN ${vin}` : ""}</div>
              <div className="text-muted-foreground text-xs mt-1 line-clamp-2">"{complaint}"</div>
            </div>
            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setStep(2)} className="min-h-12" data-testid="wizard-back-3">
                <ArrowLeft weight="bold" className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button onClick={submit} disabled={submitting} className="min-h-12 bg-primary hover:bg-primary/90" data-testid="wizard-submit">
                {submitting ? "Analyzing..." : "Run diagnostic"}
                {!submitting && <ArrowRight weight="bold" className="w-4 h-4 ml-1" />}
              </Button>
            </div>
            {submitting && (
              <div className="mt-6 border border-border rounded-md p-6 bg-background scan-line">
                <div className="font-mono-tech text-xs text-primary">// diagnostic engine thinking</div>
                <div className="font-display text-2xl mt-1">Reasoning through possible causes…</div>
                <div className="text-xs text-muted-foreground mt-2">Ranking suspects · Selecting first test · This can take 15-30 seconds</div>
              </div>
            )}
          </section>
        )}
      </div>
    </Layout>
  );
}

function Stepper({ step }) {
  const labels = ["Vehicle", "Complaint", "Observations"];
  return (
    <div className="mt-8 flex items-center gap-3">
      {labels.map((l, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <div key={l} className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-md flex items-center justify-center text-sm font-mono-tech border ${active ? "bg-primary text-white border-primary" : done ? "bg-primary/20 border-primary/40 text-primary" : "bg-secondary text-muted-foreground border-border"}`}>
              {n}
            </div>
            <div className={`font-display text-lg tracking-tight ${active ? "text-white" : "text-muted-foreground"}`}>{l}</div>
            {i < labels.length - 1 && <div className="w-8 h-px bg-border" />}
          </div>
        );
      })}
    </div>
  );
}
